import React from "react";
import PropTypes from "prop-types";
import { useLocation, useNavigate } from "react-router-dom";
import moment from "moment-timezone";
import { getSessionDisplayName, isMassageSessionType } from "../utils/sessionTypeUtils";
import { resolveGuestBadgeVariant } from "../utils/addonIndicatorUtils";
import CustomerNameLink from "./staff/CustomerNameLink";
import "./StaffSessionModal.css";

// STAFF-ADDON-VISIBILITY-002 — defensive invariant warn (ADR-042 D1):
// addGuest and doubleOccupancy are mutually exclusive at the BE policy
// layer. If a booking carries BOTH, log a console warning for staff
// visibility (R-024-class signal) and let the row render the safe "Yes"
// fallback. Mirrors the same-named local helper in StaffDashboard.js
// (PR #232) so each consumer fires its own invariant signal.
const warnDoubleOccupancyAndGuest = (booking) => {
  // eslint-disable-next-line no-console
  console.warn(
    "[STAFF-ADDON-VISIBILITY-002] Booking has both addGuest and doubleOccupancy — ADR-042 D1 invariant violation",
    { bookingId: booking && booking._id }
  );
};

// STAFF-ADDON-VISIBILITY-002 — map resolveGuestBadgeVariant() output to
// the "Add Guest:" row label rendered in the Session Details modal.
// - 'guest'     -> "Yes"          (existing $25 guest-pass path)
// - 'guestCorp' -> "Yes (Corp)"   (corp-tier $15 double-occupancy)
// - 'both'      -> "Yes" + warn   (ADR-042 D1 invariant violation)
// - 'none'      -> "No"           (regression-untouched legacy path)
const renderAddGuestLabel = (booking) => {
  const variant = resolveGuestBadgeVariant(booking);
  if (variant === "both") {
    warnDoubleOccupancyAndGuest(booking);
    return "Yes";
  }
  if (variant === "guestCorp") return "Yes (Corp)";
  if (variant === "guest") return "Yes";
  return "No";
};

/**
 * StaffSessionModal component for displaying session details and providing edit options
 * @param {Object} props - Component props
 * @param {Object} props.booking - Booking object
 * @param {Function} props.onClose - Function to call when the modal is closed
 * @returns {JSX.Element} - Rendered component
 */
const StaffSessionModal = ({ booking, onClose }) => {
  const navigate = useNavigate();
  const location = useLocation();

  if (!booking) {
    return null;
  }

  const appointment = new Date(booking.appointmentDate);
  const formattedDate = moment(appointment).format("MMMM D, YYYY");
  const formattedTime = moment(appointment).format("h:mm A");

  const sessionTypeLabel = getSessionDisplayName(booking.sessionType);
  const formattedPaymentAmount =
    typeof booking.paymentId?.amount === "number"
      ? (Number(booking.paymentId.amount) / 100).toFixed(2)
      : null;
  const formattedRefundAmount =
    typeof booking.paymentId?.refundAmount === "number" &&
    booking.paymentId.refundAmount > 0
      ? (Number(booking.paymentId.refundAmount) / 100).toFixed(2)
      : null;

  const suiteLabel = booking.suiteAssignment
    ? booking.suiteAssignment.type === "massage"
      ? `Massage ${booking.suiteAssignment.number}`
      : booking.suiteAssignment.number
    : null;

  const handleEditClick = () => {
    navigate(`/staff/edit-booking/${booking._id}`, {
      state: { returnTo: `${location.pathname}${location.search}` },
    });
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <h2>Session Details</h2>

        <div className="session-info">
          <p>
            <strong>Member:</strong>{" "}
            <CustomerNameLink customerId={booking.user?._id}>
              {booking.user?.firstName && booking.user?.lastName
                ? `${booking.user.firstName} ${booking.user.lastName}`
                : "Unknown User"}
            </CustomerNameLink>
          </p>
          <p>
            <strong>Type:</strong> {sessionTypeLabel}
          </p>
          <p>
            <strong>Date:</strong> {formattedDate}
          </p>
          <p>
            <strong>Time:</strong> {formattedTime}
          </p>
          <p>
            <strong>Status:</strong> {booking.status}
          </p>

          {booking.suiteAssignment && (
            <p>
              <strong>Suite:</strong> {suiteLabel}
              {booking.suiteAssignment.handicap ? " (ADA)" : ""}
            </p>
          )}

          {/* Sauna-specific options */}
          {booking.sessionType === "infrared" && (
            <div className="session-options">
              <p>
                <strong>Add Guest:</strong> {renderAddGuestLabel(booking)}
              </p>
              <p>
                <strong>Aromatherapy:</strong>{" "}
                {booking.aromatherapy ? "Yes" : "No"}
              </p>
              <p>
                <strong>Halotherapy:</strong>{" "}
                {booking.halotherapy ? "Yes" : "No"}
              </p>
            </div>
          )}

          {booking.paymentId && (
            <>
              {isMassageSessionType(booking.sessionType) && (
                <p>
                  <strong>Refund policy:</strong> 2+ hours for a full refund, 1-2
                  hours for a 50% refund, and under 1 hour for no refund.
                </p>
              )}
              <p>
                <strong>Payment:</strong> {booking.paymentId.status || "Paid"}
                {formattedPaymentAmount ? ` • $${formattedPaymentAmount}` : ""}
                {formattedRefundAmount
                  ? ` • Refunded $${formattedRefundAmount}`
                  : ""}
              </p>
            </>
          )}
        </div>

        <div className="modal-buttons">
          <button
            onClick={handleEditClick}
            className="action-btn action-btn-primary"
          >
            Edit Session
          </button>
          <button onClick={onClose} className="action-btn action-btn-secondary">
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

StaffSessionModal.propTypes = {
  booking: PropTypes.object,
  onClose: PropTypes.func.isRequired,
};

export default StaffSessionModal;
